/**
 * Admin · IT Solutions · Public Page CMS.
 *
 * Edits the editable copy that drives /it-solutions.
 *  - Hero (eyebrow, title lead/highlight, subtitle, CTAs, trust badge)
 *  - Categories (eyebrow, title, per-category blurbs)
 *  - Featured + Catalogue eyebrows
 *  - How we deliver (eyebrow, title, 5 steps editable)
 *  - Final CTA (title, subtitle, button)
 */

import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Save, Loader2, Globe, RefreshCw, ExternalLink, ChevronDown,
  ChevronRight, Plus, X, Eye,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { useToast } from '../../../hooks/use-toast';
import BackButton from '../../../components/admin/BackButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_LABELS = {
  business_laptops: 'Business Laptops',
  workstations: 'Workstations',
  monitors: 'Monitors',
  printers_peripherals: 'Printers & Peripherals',
  networking: 'Networking & WiFi',
  software_security: 'Software & Security',
};

const ITPublicPageEditor = () => {
  const { toast } = useToast();
  const token = useMemo(() => localStorage.getItem('admin_token'), []);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState({ hero: true, categories: true, sections: true, steps: false, cta: true });

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/admin/it-page/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(r.data);
    } catch (e) {
      toast({ variant: 'destructive', title: 'Load failed', description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSettings(); /* eslint-disable-next-line */ }, []);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setBlurb = (key, v) =>
    setForm((p) => ({ ...p, category_blurbs: { ...(p.category_blurbs || {}), [key]: v } }));
  const setStep = (i, k, v) =>
    setForm((p) => ({
      ...p,
      how_we_deliver_steps: (p.how_we_deliver_steps || []).map((s, idx) => (idx === i ? { ...s, [k]: v } : s)),
    }));
  const addStep = () =>
    setForm((p) => {
      const arr = p.how_we_deliver_steps || [];
      const nextN = String(arr.length + 1).padStart(2, '0');
      return { ...p, how_we_deliver_steps: [...arr, { n: nextN, title: '', desc: '' }] };
    });
  const removeStep = (i) =>
    setForm((p) => ({ ...p, how_we_deliver_steps: (p.how_we_deliver_steps || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      // Strip read-only fields from payload
      const payload = { ...form };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.updated_by;
      const r = await axios.put(`${API_URL}/api/admin/it-page/settings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(r.data);
      toast({ title: 'Saved', description: 'Public page updated.' });
    } catch (e) {
      toast({ variant: 'destructive', title: 'Save failed', description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="p-10 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#023E8A]" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto pb-32">
      <div className="mb-4"><BackButton to="/admin/it-catalogue" label="Catalogue" /></div>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Globe className="w-6 h-6 text-[#023E8A]" />
            <h1 className="text-2xl font-bold text-[#2B2D42]">Public Page · CMS</h1>
            <Badge className="bg-[#023E8A]/10 text-[#023E8A] border border-[#023E8A]/30 font-mono text-[10px]">P0.7</Badge>
          </div>
          <p className="text-sm text-[#6C757D]">
            Edit hero, categories, sections, how-we-deliver and CTA copy on
            <a href="/it-solutions" target="_blank" rel="noreferrer" className="text-[#00B4D8] hover:underline ml-1">/it-solutions <ExternalLink className="inline w-3 h-3" /></a>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchSettings} disabled={saving}>
            <RefreshCw className="w-4 h-4 mr-2" /> Reload
          </Button>
          <a href="/it-solutions" target="_blank" rel="noreferrer">
            <Button variant="outline" data-testid="cms-preview-btn">
              <Eye className="w-4 h-4 mr-2" /> Preview page
            </Button>
          </a>
        </div>
      </div>

      {/* Hero */}
      <Card open={open.hero} onToggle={() => setOpen({ ...open, hero: !open.hero })} title="Hero" subtitle="Top section with the headline">
        <Grid cols={2}>
          <Field label="Eyebrow (small badge above title)" value={form.hero_eyebrow} onChange={(v) => set('hero_eyebrow', v)} testid="cms-hero-eyebrow" />
          <Field label="Trust badge (small chip in the right panel)" value={form.trust_badge} onChange={(v) => set('trust_badge', v)} testid="cms-trust-badge" />
        </Grid>
        <Grid cols={2}>
          <Field label="Title — lead" value={form.hero_title_lead} onChange={(v) => set('hero_title_lead', v)} placeholder="IT Solutions for" testid="cms-title-lead" />
          <Field label="Title — highlight (gradient)" value={form.hero_title_highlight} onChange={(v) => set('hero_title_highlight', v)} placeholder="modern enterprises" testid="cms-title-highlight" />
        </Grid>
        <Field label="Subtitle">
          <textarea rows={3} value={form.hero_subtitle || ''} onChange={(e) => set('hero_subtitle', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cms-subtitle" />
        </Field>
        <Grid cols={2}>
          <Field label="Primary CTA button" value={form.hero_cta_primary} onChange={(v) => set('hero_cta_primary', v)} testid="cms-cta-primary" />
          <Field label="Secondary CTA link" value={form.hero_cta_secondary} onChange={(v) => set('hero_cta_secondary', v)} testid="cms-cta-secondary" />
        </Grid>
      </Card>

      {/* Categories */}
      <Card open={open.categories} onToggle={() => setOpen({ ...open, categories: !open.categories })} title="Categories section" subtitle="Section header + per-category blurbs">
        <Grid cols={2}>
          <Field label="Eyebrow" value={form.categories_eyebrow} onChange={(v) => set('categories_eyebrow', v)} testid="cms-cats-eyebrow" />
          <Field label="Title" value={form.categories_title} onChange={(v) => set('categories_title', v)} testid="cms-cats-title" />
        </Grid>
        <div className="space-y-2 mt-2">
          {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
            <div key={key} className="grid grid-cols-1 sm:grid-cols-[260px_1fr] gap-3 items-start">
              <div className="text-[11px] uppercase tracking-widest font-bold text-[#6C757D] pt-3">{label}</div>
              <textarea
                rows={2}
                value={(form.category_blurbs || {})[key] || ''}
                onChange={(e) => setBlurb(key, e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                data-testid={`cms-blurb-${key}`}
              />
            </div>
          ))}
        </div>
      </Card>

      {/* Featured + Catalogue eyebrows */}
      <Card open={open.sections} onToggle={() => setOpen({ ...open, sections: !open.sections })} title="Featured & Catalogue section headers" subtitle="Two short labels above the product grids">
        <Grid cols={2}>
          <Field label="Featured · eyebrow" value={form.featured_eyebrow} onChange={(v) => set('featured_eyebrow', v)} testid="cms-featured-eyebrow" />
          <Field label="Featured · title" value={form.featured_title} onChange={(v) => set('featured_title', v)} testid="cms-featured-title" />
          <Field label="Catalogue · eyebrow" value={form.catalogue_eyebrow} onChange={(v) => set('catalogue_eyebrow', v)} testid="cms-catalogue-eyebrow" />
        </Grid>
      </Card>

      {/* How we deliver */}
      <Card open={open.steps} onToggle={() => setOpen({ ...open, steps: !open.steps })} title="How we deliver" subtitle="5-step timeline (RFQ → Setup)">
        <Grid cols={2}>
          <Field label="Eyebrow" value={form.how_we_deliver_eyebrow} onChange={(v) => set('how_we_deliver_eyebrow', v)} testid="cms-hwd-eyebrow" />
          <Field label="Title" value={form.how_we_deliver_title} onChange={(v) => set('how_we_deliver_title', v)} testid="cms-hwd-title" />
        </Grid>
        <div className="space-y-2 mt-3">
          {(form.how_we_deliver_steps || []).map((s, i) => (
            <div key={i} className="grid grid-cols-1 sm:grid-cols-[80px_220px_1fr_auto] gap-2 items-center">
              <input
                value={s.n || ''}
                onChange={(e) => setStep(i, 'n', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono text-center"
                placeholder="01"
                data-testid={`cms-step-n-${i}`}
              />
              <input
                value={s.title || ''}
                onChange={(e) => setStep(i, 'title', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-bold"
                placeholder="Title"
                data-testid={`cms-step-title-${i}`}
              />
              <input
                value={s.desc || ''}
                onChange={(e) => setStep(i, 'desc', e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
                placeholder="Description"
                data-testid={`cms-step-desc-${i}`}
              />
              <button onClick={() => removeStep(i)} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addStep} data-testid="cms-add-step-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Add step
          </Button>
        </div>
      </Card>

      {/* Final CTA */}
      <Card open={open.cta} onToggle={() => setOpen({ ...open, cta: !open.cta })} title="Final CTA" subtitle="Dark gradient call-to-action at the bottom">
        <Field label="Title" value={form.final_cta_title} onChange={(v) => set('final_cta_title', v)} testid="cms-final-cta-title" />
        <Field label="Subtitle">
          <textarea rows={3} value={form.final_cta_subtitle || ''} onChange={(e) => set('final_cta_subtitle', e.target.value)} className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" data-testid="cms-final-cta-subtitle" />
        </Field>
        <Field label="Button label" value={form.final_cta_button} onChange={(v) => set('final_cta_button', v)} testid="cms-final-cta-btn" />
      </Card>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-100 shadow-lg px-6 py-3 lg:left-64">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <span className="text-[11px] text-[#6C757D]">
            {form.updated_at ? `Last update: ${new Date(form.updated_at).toLocaleString('en-GB')}` : 'No saved version yet — saving will publish.'}
          </span>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#FF6B35] hover:bg-[#E5562D] text-white font-bold"
            data-testid="cms-save-btn"
          >
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</> : <><Save className="w-4 h-4 mr-2" /> Save & publish</>}
          </Button>
        </div>
      </div>
    </div>
  );
};

const Card = ({ open, onToggle, title, subtitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 mb-4 overflow-hidden">
    <button onClick={onToggle} className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 text-left">
      <div>
        <h3 className="text-base font-extrabold text-[#2B2D42]">{title}</h3>
        <p className="text-[12px] text-[#6C757D] mt-0.5">{subtitle}</p>
      </div>
      {open ? <ChevronDown className="w-5 h-5 text-[#6C757D]" /> : <ChevronRight className="w-5 h-5 text-[#6C757D]" />}
    </button>
    {open && <div className="px-6 pb-6 border-t border-slate-100 pt-4 space-y-4">{children}</div>}
  </div>
);

const Grid = ({ cols = 2, children }) => (
  <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-4`}>{children}</div>
);

const Field = ({ label, value, onChange, placeholder, testid, children }) => (
  <div>
    <label className="text-[11px] font-bold uppercase tracking-widest text-[#6C757D]">{label}</label>
    {children || (
      <input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
        data-testid={testid}
      />
    )}
  </div>
);

export default ITPublicPageEditor;
